import {
    createReactAdapter,
    type ReactDesignComponentType,
} from '@salesforce/storefront-next-runtime/design/react/core';
import { ComponentRegistry } from '@salesforce/storefront-next-runtime/design';

type BaseReactComponentRegistry<TProps> = ComponentRegistry<TProps, ReactDesignComponentType<TProps>>;
type RegisterImporterArgs<TProps> = Parameters<BaseReactComponentRegistry<TProps>['registerImporter']>;
type RegisterComponentArgs<TProps> = Parameters<BaseReactComponentRegistry<TProps>['registerComponent']>;
type ComponentId<TProps> = RegisterImporterArgs<TProps>[0];
type ComponentImporter<TProps> = RegisterImporterArgs<TProps>[1];

class ReactComponentRegistry<TProps> extends ComponentRegistry<TProps, ReactDesignComponentType<TProps>> {
    private readonly importers = new Map<ComponentId<TProps>, ComponentImporter<TProps>>();
    private readonly eagerPending = new Map<ComponentId<TProps>, Promise<void>>();
    private readonly eagerComponents = new Set<ComponentId<TProps>>();
    private readonly decoratedComponents = new Map<ComponentId<TProps>, ReactDesignComponentType<TProps>>();

    override registerImporter(...args: RegisterImporterArgs<TProps>): void {
        const [id, importer] = args;
        this.importers.set(id, importer);
        // Route loaders may initialize the generated registry concurrently.
        // Do not replace a component that another loader already made synchronous
        // for critical SSR, otherwise rendering falls back to lazy suspension.
        if (this.eagerComponents.has(id)) return;
        this.decoratedComponents.delete(id);
        super.registerImporter(...args);
    }

    override registerComponent(...args: RegisterComponentArgs<TProps>): void {
        this.decoratedComponents.delete(args[0]);
        super.registerComponent(...args);
    }

    override getComponent(id: ComponentId<TProps>) {
        const cached = this.decoratedComponents.get(id);
        if (cached) return cached;

        // The runtime adapter creates a decorated component wrapper on every
        // call. Cache that wrapper so React does not remount the subtree when
        // a parent renders again.
        const component = super.getComponent(id);
        if (component) this.decoratedComponents.set(id, component);
        return component;
    }

    /**
     * Loads and registers the raw component so React can render it synchronously
     * during the initial server pass instead of crossing a React.lazy boundary.
     */
    async preloadEager(id: ComponentId<TProps>): Promise<void> {
        const existing = this.eagerPending.get(id);
        if (existing) return existing;

        const importer = this.importers.get(id);
        if (!importer) {
            return this.preload(id);
        }

        const pending = importer()
            .then((module) => {
                this.registerComponent(id, module.default);
                this.eagerComponents.add(id);
            })
            .finally(() => {
                this.eagerPending.delete(id);
            });
        this.eagerPending.set(id, pending);
        return pending;
    }
}

/**
 * Factory function to create a React-specific component registry
 * with the React adapter pre-configured.
 */
export function createReactComponentRegistry<TProps>() {
    return new ReactComponentRegistry<TProps>({
        adapter: createReactAdapter<TProps>(),
    });
}

/**
 * Global component registry instance.
 * Used throughout the application to discover and load components.
 *
 * This singleton instance is configured with:
 * - React adapter for React-specific behavior
 * - Design mode decorator for Page Designer integration
 * - Static component registration via Vite plugin (no dynamic discovery needed)
 * - Component metadata handled via API (not stored in registry)
 */
// We don't care about the type of props of the components.
// Just ignore them or else any combination of props won't be allowed.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type GlobalRegistry = ReturnType<typeof createReactComponentRegistry<any>>;
const globalRegistry = globalThis as typeof globalThis & {
    __eatalyPageDesignerRegistry?: GlobalRegistry;
};

export const registry =
    globalRegistry.__eatalyPageDesignerRegistry ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalRegistry.__eatalyPageDesignerRegistry = createReactComponentRegistry<any>());
